/** Shared state vocabulary (BRD Section 17.2). UI and CLI use the same names (NFR-UX-01). */

export const VPS_ENROLLMENT_STATES = ['registered', 'validating', 'awaiting_approval', 'preparing', 'ready', 'preparation_failed', 'removed'] as const;
export type VpsEnrollmentState = (typeof VPS_ENROLLMENT_STATES)[number];

export const VPS_CONNECTION_STATES = ['connected', 'disconnected', 'stale', 'maintenance', 'never'] as const;
export type VpsConnectionState = (typeof VPS_CONNECTION_STATES)[number];

export const REGISTRATION_STATES = ['local', 'pending', 'registered', 'failed'] as const;
export type RegistrationState = (typeof REGISTRATION_STATES)[number];

export const DEPLOY_STAGES = ['validating', 'building', 'pulling', 'configuring', 'applying', 'verifying'] as const;
export type DeployStage = (typeof DEPLOY_STAGES)[number];

export const JOB_STATES = [
  'queued',
  'running',
  'awaiting_approval',
  'succeeded',
  'failed',
  'canceled',
  'unknown',
] as const;
export type JobState = (typeof JOB_STATES)[number];
export const TERMINAL_JOB_STATES: JobState[] = ['succeeded', 'failed', 'canceled'];

export function isTerminalJobState(state: string): boolean {
  return (TERMINAL_JOB_STATES as string[]).includes(state);
}

/** Deployment attempt state as described by the BRD: the running stage name, or a terminal state. */
export type DeploymentAttemptState = 'queued' | DeployStage | 'succeeded' | 'failed' | 'canceled' | 'unknown';

export const JOB_KINDS = [
  'deploy',
  'lifecycle',
  'apply-config',
  'rollback',
  'reroute',
  'remove',
  'delete-data',
  'backup',
  'restore',
  'enroll',
  'agent-upgrade',
  'vps-remove',
  'ssh-ca-install',
  'ssh-ca-remove',
  'migrate',
  'migrate-transfer',
  'migrate-cutover',
] as const;
export type JobKind = (typeof JOB_KINDS)[number];

export const STAGE_STATES = ['pending', 'running', 'succeeded', 'failed', 'skipped', 'canceled'] as const;
export type StageState = (typeof STAGE_STATES)[number];

export const PROCESS_STATES = ['running', 'partial', 'stopped', 'not-deployed', 'unknown'] as const;
export type ProcessState = (typeof PROCESS_STATES)[number];

/**
 * Process state of a deployed stack from its observed services. Setup services (`runOnce`) that
 * exited with code 0 are completed, not stopped, so they never make a stack partially running;
 * a failed setup service (non-zero exit) still counts as not running.
 */
export function summarizeProcess(services: { state: string; exitCode?: number; runOnce?: boolean }[]): { process: ProcessState; running: number } {
  const relevant = services.filter((s) => !(s.runOnce && s.state === 'exited' && s.exitCode === 0));
  const running = relevant.filter((s) => s.state === 'running').length;
  const process: ProcessState = running === 0 ? 'stopped' : running === relevant.length ? 'running' : 'partial';
  return { process, running };
}

export const HEALTH_STATES = ['healthy', 'unhealthy', 'starting', 'unknown', 'none'] as const;
export type HealthState = (typeof HEALTH_STATES)[number];

export const DNS_STATES = ['ok', 'pending', 'mismatch', 'unknown'] as const;
export type DnsState = (typeof DNS_STATES)[number];

export const CERT_STATES = ['valid', 'pending', 'failed', 'unknown', 'n/a'] as const;
export type CertState = (typeof CERT_STATES)[number];

export const BACKUP_STATES = ['creating', 'failed', 'created', 'verified', 'expired', 'removed'] as const;
export type BackupState = (typeof BACKUP_STATES)[number];

/** Seconds after which an observation is considered stale rather than live. */
export const OBSERVATION_STALE_SECONDS = 60;

/** Compose project name used as the stable ownership identity of a deployment target. */
export function stackName(projectKey: string, environment: string, instance = 'default'): string {
  const base = `drb-${projectKey}-${environment}`;
  return instance === 'default' ? base : `${base}-${instance}`;
}

/** Local Compose project names cannot collide with remote stacks or other local projects (FR-LOCAL-02). */
export function localStackName(projectKey: string, mode: 'dev' | 'run'): string {
  return `drb-local-${projectKey}-${mode}`;
}
